import React, { useCallback, useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import classNames from 'classnames'

import type { FloatingPosition, FloatingSize } from './AgentChatContext'
import ChatWindowHeader from './ChatWindowHeader'

import '@chewy/kib-controls-styles/dist/styles/kib-button-new.css'
import styles from './DraggableResizableWindow.module.scss'

type ResizeDirection =
  | 'left'
  | 'right'
  | 'bottom'
  | 'bottom-left'
  | 'bottom-right'

interface DraggableResizableWindowProps {
  children: React.ReactNode
  onClose: () => void
  onPositionChange: (position: FloatingPosition) => void
  onSizeChange: (size: FloatingSize) => void
  onToggleMode?: () => void
  position: FloatingPosition
  size: FloatingSize
}

const DraggableResizableWindow: React.FC<DraggableResizableWindowProps> = ({
  children,
  position,
  size,
  onPositionChange,
  onSizeChange,
  onClose,
  onToggleMode,
}) => {
  const { t } = useTranslation('Agent')
  const [isDragging, setIsDragging] = useState(false)
  const [isResizing, setIsResizing] = useState(false)
  const [resizeDirection, setResizeDirection] =
    useState<ResizeDirection | null>(null)
  const [dragStart, setDragStart] = useState({ x: 0, y: 0 })
  const [resizeStart, setResizeStart] = useState({
    x: 0,
    y: 0,
    width: 0,
    height: 0,
    positionX: 0,
  })

  const windowRef = useRef<HTMLDivElement>(null)

  const constrainPosition = useCallback(
    (pos: FloatingPosition): FloatingPosition => {
      const minX = 0
      const maxX = window.innerWidth - size.width
      const minY = 0
      const maxY = window.innerHeight - size.height

      return {
        x: Math.max(minX, Math.min(pos.x, maxX)),
        y: Math.max(minY, Math.min(pos.y, maxY)),
      }
    },
    [size.width, size.height],
  )

  const constrainSize = useCallback(
    (
      newSize: FloatingSize,
      newPosition?: FloatingPosition,
      direction?: ResizeDirection | null,
      originalSize?: FloatingSize,
      originalPosition?: FloatingPosition,
    ): { position: FloatingPosition; size: FloatingSize } => {
      const minWidth = 350
      const minHeight = 310
      const currentPos = newPosition ?? position
      const originalWidth = originalSize?.width ?? size.width
      const originalX = originalPosition?.x ?? position.x

      let constrainedWidth: number
      let constrainedHeight = newSize.height
      let constrainedX = currentPos.x
      const constrainedY = currentPos.y

      // Handle left edge and left corners
      if (direction === 'left' || direction === 'bottom-left') {
        // Max width is the distance from original left edge to right edge of window,
        // but it must not exceed the viewport width.
        const maxWidth = Math.min(originalX + originalWidth, window.innerWidth)
        constrainedWidth = Math.max(minWidth, Math.min(newSize.width, maxWidth))
        // Adjust X position when resizing from left
        constrainedX = originalX + originalWidth - constrainedWidth
        // Ensure X doesn't go negative
        if (constrainedX < 0) {
          // Recalculate width when hitting left edge, but still enforce minWidth
          const maxWidthAtLeftEdge = Math.min(
            originalX + originalWidth,
            window.innerWidth,
          )
          constrainedWidth = Math.max(minWidth, maxWidthAtLeftEdge)
          constrainedX = 0
        }
      } else {
        // Right edge and right corners
        const maxWidth = window.innerWidth - currentPos.x
        constrainedWidth = Math.max(minWidth, Math.min(newSize.width, maxWidth))
      }

      // Bottom edge and corners
      if (
        direction === 'bottom' ||
        direction === 'bottom-left' ||
        direction === 'bottom-right'
      ) {
        const maxHeight = window.innerHeight - currentPos.y
        constrainedHeight = Math.max(
          minHeight,
          Math.min(newSize.height, maxHeight),
        )
      }

      return {
        size: {
          width: constrainedWidth,
          height: constrainedHeight,
        },
        position: {
          x: constrainedX,
          y: constrainedY,
        },
      }
    },
    [position.x, position.y, size.width, size.height],
  )

  const handleMouseDown = useCallback(
    (e: React.MouseEvent) => {
      setIsDragging(true)
      setDragStart({
        x: e.clientX - position.x,
        y: e.clientY - position.y,
      })
      e.preventDefault()
    },
    [position.x, position.y],
  )

  const handleMouseMove = useCallback(
    (e: MouseEvent) => {
      if (isDragging) {
        const newPosition = constrainPosition({
          x: e.clientX - dragStart.x,
          y: e.clientY - dragStart.y,
        })
        onPositionChange(newPosition)
      } else if (isResizing && resizeDirection) {
        const deltaX = e.clientX - resizeStart.x
        const deltaY = e.clientY - resizeStart.y

        let newWidth = resizeStart.width
        let newHeight = resizeStart.height
        let newX = resizeStart.positionX

        // Calculate new dimensions based on resize direction
        if (resizeDirection === 'left' || resizeDirection === 'bottom-left') {
          newWidth = resizeStart.width - deltaX
          newX = resizeStart.positionX + deltaX
        } else if (
          resizeDirection === 'right' ||
          resizeDirection === 'bottom-right'
        ) {
          newWidth = resizeStart.width + deltaX
        }

        if (
          resizeDirection === 'bottom' ||
          resizeDirection === 'bottom-left' ||
          resizeDirection === 'bottom-right'
        ) {
          newHeight = resizeStart.height + deltaY
        }

        const constrained = constrainSize(
          { width: newWidth, height: newHeight },
          { x: newX, y: position.y },
          resizeDirection,
          { width: resizeStart.width, height: resizeStart.height },
          { x: resizeStart.positionX, y: position.y },
        )

        onSizeChange(constrained.size)
        // Update position for left edge resizing
        if (resizeDirection === 'left' || resizeDirection === 'bottom-left') {
          onPositionChange(constrained.position)
        }
      }
    },
    [
      isDragging,
      dragStart,
      isResizing,
      resizeDirection,
      resizeStart,
      position.y,
      constrainPosition,
      constrainSize,
      onPositionChange,
      onSizeChange,
    ],
  )

  const handleMouseUp = useCallback(() => {
    setIsDragging(false)
    setIsResizing(false)
    setResizeDirection(null)
  }, [])

  const handleResizeStart = useCallback(
    (direction: ResizeDirection) => (e: React.MouseEvent) => {
      setIsResizing(true)
      setResizeDirection(direction)
      setResizeStart({
        x: e.clientX,
        y: e.clientY,
        width: size.width,
        height: size.height,
        positionX: position.x,
      })
      e.preventDefault()
      e.stopPropagation()
    },
    [size.width, size.height, position.x],
  )

  useEffect(() => {
    if (isDragging || isResizing) {
      document.addEventListener('mousemove', handleMouseMove)
      document.addEventListener('mouseup', handleMouseUp)

      // Lock cursor on document body during resize to prevent flickering
      if (isResizing && resizeDirection) {
        let cursorStyle = ''
        switch (resizeDirection) {
          case 'left':
          case 'right':
            cursorStyle = 'ew-resize'
            break
          case 'bottom':
            cursorStyle = 'ns-resize'
            break
          case 'bottom-left':
            cursorStyle = 'sw-resize'
            break
          case 'bottom-right':
            cursorStyle = 'se-resize'
            break
          default:
            cursorStyle = 'default'
            break
        }
        document.body.style.cursor = cursorStyle
      } else if (isDragging) {
        document.body.style.cursor = 'move'
      }

      return () => {
        document.removeEventListener('mousemove', handleMouseMove)
        document.removeEventListener('mouseup', handleMouseUp)
        document.body.style.cursor = ''
      }
    }
    return undefined
  }, [isDragging, isResizing, resizeDirection, handleMouseMove, handleMouseUp])

  return (
    <div
      className={classNames(styles.floatingWindow, {
        [styles.dragging]: isDragging,
        [styles.resizing]: isResizing,
      })}
      ref={windowRef}
      style={{
        left: position.x,
        top: position.y,
        width: size.width,
        height: size.height,
      }}
    >
      {/* Window header with drag handle and controls */}
      <ChatWindowHeader
        draggable
        isFloating
        title={t('Agent:AI_ASSISTANT')}
        onClose={onClose}
        onMouseDown={handleMouseDown}
        onToggleMode={onToggleMode}
      />

      {/* Resize handles */}
      {/* Left edge */}
      <div
        aria-label={t('RESIZE_WINDOW')}
        className={classNames(styles.resizeHandle, styles.resizeHandleLeft)}
        role="button"
        tabIndex={0}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault()
          }
        }}
        onMouseDown={handleResizeStart('left')}
      />

      {/* Right edge */}
      <div
        aria-label={t('RESIZE_WINDOW')}
        className={classNames(styles.resizeHandle, styles.resizeHandleRight)}
        role="button"
        tabIndex={0}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault()
          }
        }}
        onMouseDown={handleResizeStart('right')}
      />

      {/* Bottom edge */}
      <div
        aria-label={t('RESIZE_WINDOW')}
        className={classNames(styles.resizeHandle, styles.resizeHandleBottom)}
        role="button"
        tabIndex={0}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault()
          }
        }}
        onMouseDown={handleResizeStart('bottom')}
      />

      {/* Bottom-left corner */}
      <div
        aria-label={t('RESIZE_WINDOW')}
        className={classNames(
          styles.resizeHandle,
          styles.resizeHandleBottomLeft,
        )}
        role="button"
        tabIndex={0}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault()
          }
        }}
        onMouseDown={handleResizeStart('bottom-left')}
      />

      {/* Bottom-right corner */}
      <div
        aria-label={t('RESIZE_WINDOW')}
        className={classNames(
          styles.resizeHandle,
          styles.resizeHandleBottomRight,
        )}
        role="button"
        tabIndex={0}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault()
          }
        }}
        onMouseDown={handleResizeStart('bottom-right')}
      />

      {/* Window content */}
      <div className={styles.windowContent}>{children}</div>
    </div>
  )
}

export default DraggableResizableWindow
